/* eslint-disable react/prop-types */
import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  List,
  Divider,
  Typography,
  Tooltip,
  IconButton,
  SvgIcon,
  Collapse,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SidebarItem from "./SidebarItem";

// Import icons
import HomeIcon from "../../assets/images/dashboard1.svg";
import ShareholderIcon from "../../assets/images/shareholder.svg";
import ApprovedIcon from "../../assets/images/approved.svg";
import RejectedIcon from "../../assets/images/rejected.svg";
import ELpendingIcon from "../../assets/images/ELpending.svg";
import eVerificationIcon from "../../assets/images/everification.svg";

import { ROUTES, USER_TYPE } from "../../constants";
import AppImage from "../../assets/images/IRIS-logo-Transprenet-white.png";

// Memoized PinIcon component
const PinIcon = React.memo(({ filled = false, ...props }) => (
  <SvgIcon {...props}>
    {filled ? (
      <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z" />
    ) : (
      <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12M14,14H10L11,13V4H13V13L14,14Z" />
    )}
  </SvgIcon>
));

PinIcon.displayName = "PinIcon";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    justifyContent: "space-between",
  },
  logoContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    flexDirection: (props) => (props.isCollapsed ? "column" : "row"),
    minHeight: "auto",
  },
  logoImage: {
    width: (props) => (props.isCollapsed ? "60px" : "30%"),
    height: "auto",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  sidebarHeader: {
    padding: theme.spacing(0),
    textAlign: "center",
    color: theme.palette.primary.contrastText,
    display: (props) => (props.isCollapsed ? "none" : "block"),
    transition: theme.transitions.create("opacity", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  pinButton: {
    color: theme.palette.primary.contrastText,
    padding: "4px",
    transition: theme.transitions.create(["right", "transform"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  pinnedIcon: {
    color: "white",
  },
  unpinnedIcon: {
    color: theme.palette.primary.contrastText,
    opacity: 0.7,
  },
  headerSection: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    flexDirection: (props) => (props.isCollapsed ? "column" : "row"),
    padding: theme.spacing(1, 1),
  },
  collapsedDivider: {
    margin: theme.spacing(1, 0),
  },
  listContainer: {
    paddingTop: 0,
    paddingBottom: 0,
  },
}));

// Main menu items configuration
const MENU_ITEMS = [
  {
    label: "Dashboard",
    key: "dashboard",
    icon: HomeIcon,
    path: ROUTES.DASHBOARD,
    roles: [USER_TYPE.PERFORMER, USER_TYPE.APPROVER,USER_TYPE.AUDITOR],
  },
  {
    label: "RTA Form",
    key: "rtaFrom",
    icon: ELpendingIcon,
    path: ROUTES.IEPFReport,
    roles: [USER_TYPE.RTA],
  },

  {
    label: "Ticket Status",
    key: "ticketStatus",
    icon: ApprovedIcon,
    path: ROUTES.TICKET_STATUS,
    roles: [USER_TYPE.RTA, USER_TYPE.PERFORMER, USER_TYPE.APPROVER, USER_TYPE.AUDITOR],
  },
  // {
  //   label: "Approver Pending",
  //   key: "approver_pending",
  //   icon: ShareholderIcon,
  //   path: ROUTES.APPROVER_PENDING,
  //   roles: [USER_TYPE.PERFORMER],
  // },
  // {
  //   label: "Approver Rejected",
  //   key: "approver_rejected",
  //   icon: RejectedIcon,
  //   path: ROUTES.APPROVER_REJECTED,
  //   roles: [USER_TYPE.APPROVER],
  // },
];

// Collapsible sections configuration - easily extensible for future sections
const COLLAPSIBLE_SECTIONS = [
  // RTA Section - For future use
  // {
  //   key: "rta",
  //   label: "RTA",
  //   icon: ELpendingIcon,
  //   items: [
  //     {
  //       label: "RTA Form",
  //       key: "rtaForm",
  //       path: ROUTES.IEPFReport,
  //       icon: ELpendingIcon,
  //       roles: [USER_TYPE.RTA, USER_TYPE.PERFORMER],
  //     },
  //     {
  //       label: "RTA Rectify",
  //       key: "rtaRectify",
  //       icon: ELpendingIcon,
  //       path: ROUTES.TICKET_STATUS,
  //       roles: [],
  //     },
  //     {
  //       label: "RTA Reject",
  //       key: "rtaReject",
  //       icon: ELpendingIcon,
  //       path: ROUTES.RTA_REJECT,
  //       roles: [],
  //     },
  //   ],
  // },

  {
    key: "approverActionType",
    label: "Action Type",
    icon: ApprovedIcon,
    items: [
      {
        label: "Approver Queue",
        key: "pending",
        icon: ShareholderIcon,
        path: ROUTES.APPROVER_PENDING,
        roles: [USER_TYPE.APPROVER],
      },
      {
        label: "Close Ticket",
        key: "close",
        icon: RejectedIcon,
        path: ROUTES.APPROVER_CLOSE,
        roles: [USER_TYPE.APPROVER],
      },
    ],
  },

    {
    key: "rtaActionType",
    label: "Action Type",
    icon: ApprovedIcon,
    items: [
      {
        label: "RTA Rectify",
        key: "RTA-Rectify",
        icon: ShareholderIcon,
        path: ROUTES.RTA_RECTIFY,
        roles: [USER_TYPE.RTA],
      },
    ],
  },

  // Action Type Section
  {
    key: "performerActionType",
    label: "Action Type",
    icon: ApprovedIcon,
    items: [
      {
        label: "Checker Queue",
        key: "pending",
        icon: ShareholderIcon,
        path: ROUTES.PERFORMER_PENDING,
        roles: [USER_TYPE.PERFORMER],
      },
      // {
      //   label: "In Approval",
      //   key: "in-approval",
      //   icon: ELpendingIcon,
      //   path: ROUTES.PERFORMER_IN_APPROVAL,
      //   roles: [USER_TYPE.PERFORMER],
      // },
      {
        label: "Rejected",
        key: "rejected",
        icon: RejectedIcon,
        path: ROUTES.PERFORMER_REJECTED,
        roles: [USER_TYPE.PERFORMER],
      },
      {
        label: "Update EL",
        key: "el-process",
        icon: ELpendingIcon,
        path: ROUTES.PERFORMER_EL_PROCESS,
        roles: [USER_TYPE.PERFORMER],
      },
      {
        label: "Update EVR",
        key: "pending-srn",
        icon: ELpendingIcon,
        path: ROUTES.PERFORMER_PENDING_SRN,
        roles: [USER_TYPE.PERFORMER],
      },
      {
        label: "e-Verification",
        key: "e-Verification",
        icon: eVerificationIcon,
        path: ROUTES.PERFORMER_E_VERIFICATION,
        roles: [USER_TYPE.PERFORMER],
      },
    ],
  },
];

const SidebarContent = ({
  isRouteActive,
  handleNavigation,
  isCollapsed = false,
  isPinned = false,
  onPinToggle,
}) => {
  const classes = useStyles({ isCollapsed });
  const roles = useSelector((state) => state.auth.user?.roles || []);

  // Dynamic state management for all collapsible sections
  const [sectionStates, setSectionStates] = useState(
    COLLAPSIBLE_SECTIONS.reduce((acc, section) => {
      acc[section.key] = true; // All sections closed by default
      return acc;
    }, {})
  );

  // Memoized role checking function
  const hasRequiredRole = useCallback(
    (requiredRoles = []) =>
      requiredRoles.length === 0 ||
      roles.some((role) => requiredRoles.includes(role)),
    [roles]
  );

  // Memoized filtered menu items
  const filteredMenuItems = useMemo(
    () => MENU_ITEMS.filter((item) => hasRequiredRole(item.roles)),
    [hasRequiredRole]
  );

  // Memoized filtered collapsible sections
  const filteredCollapsibleSections = useMemo(
    () =>
      COLLAPSIBLE_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => hasRequiredRole(item.roles)),
      })).filter((section) => section.items.length > 0),
    [hasRequiredRole]
  );

  // Generic section toggle handler
  const handleSectionToggle = useCallback((sectionKey) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }, []);

  // Memoized pin button component
  const PinButton = useMemo(
    () => (
      <Tooltip
        title={isPinned ? "Unpin sidebar" : "Pin sidebar"}
        placement="bottom"
      >
        <IconButton
          className={classes.pinButton}
          onClick={onPinToggle}
          size="small"
        >
          <PinIcon
            filled={isPinned}
            className={isPinned ? classes.pinnedIcon : classes.unpinnedIcon}
            fontSize="small"
          />
        </IconButton>
      </Tooltip>
    ),
    [
      isPinned,
      onPinToggle,
      classes.pinButton,
      classes.pinnedIcon,
      classes.unpinnedIcon,
    ]
  );

  // Memoized menu section renderer
  const renderMenuSection = useCallback(
    (items, isSubItems = false) => (
      <List className={classes.listContainer}>
        {items.map((item) => {
          const menuItem = (
            <SidebarItem
              key={item.key}
              text={item.label}
              icon={item.icon}
              to={item.path}
              active={isRouteActive(item.path)}
              onClick={() => handleNavigation(item.path)}
              isSubItem={isSubItems}
              isCollapsed={isCollapsed}
            />
          );

          return isCollapsed ? (
            <Tooltip key={item.key} title={item.label} placement="right">
              {menuItem}
            </Tooltip>
          ) : (
            menuItem
          );
        })}
      </List>
    ),
    [isRouteActive, handleNavigation, isCollapsed, classes.listContainer]
  );

  // Generic collapsible section renderer
  const renderCollapsibleSection = useCallback(
    (section) => {
      const isOpen = sectionStates[section.key];

      return (
        <div key={section.key} className={classes.listContainer}>
          <SidebarItem
            text={section.label}
            icon={section.icon}
            to="#"
            active={false}
            onClick={() => handleSectionToggle(section.key)}
            isSubItem={false}
            isCollapsed={isCollapsed}
            hasCollapseIcon
            isExpanded={isOpen}
          />

          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List className={classes.listContainer}>
              {section.items.map((item) => {
                const menuItem = (
                  <SidebarItem
                    key={item.key}
                    text={item.label}
                    icon={item.icon}
                    to={item.path}
                    active={isRouteActive(item.path)}
                    onClick={() => handleNavigation(item.path)}
                    isSubItem
                    isCollapsed={isCollapsed}
                  />
                );

                return isCollapsed ? (
                  <Tooltip key={item.key} title={item.label} placement="right">
                    <div>{menuItem}</div>
                  </Tooltip>
                ) : (
                  menuItem
                );
              })}
            </List>
          </Collapse>
        </div>
      );
    },
    [
      sectionStates,
      isCollapsed,
      isRouteActive,
      handleNavigation,
      handleSectionToggle,
      classes.listContainer,
    ]
  );

  // Memoized collapsible sections
  const collapsibleSections = useMemo(
    () => filteredCollapsibleSections.map(renderCollapsibleSection),
    [filteredCollapsibleSections, renderCollapsibleSection]
  );

  return (
    <div className={classes.root}>
      <div>
        {/* Header Section */}
        <div className={classes.headerSection}>
          <div className={classes.logoContainer}>
            <img src={AppImage} alt="IRIS Logo" className={classes.logoImage} />
            <Typography variant="h6" className={classes.sidebarHeader}>
              <strong>Portal</strong>
            </Typography>
          </div>
          {!isCollapsed && PinButton}
        </div>

        <Divider className={isCollapsed ? classes.collapsedDivider : ""} />

        {/* Main Menu Items */}
        {renderMenuSection(filteredMenuItems)}

        {/* All Collapsible Sections */}
        {collapsibleSections}
      </div>
    </div>
  );
};

SidebarContent.propTypes = {
  isRouteActive: PropTypes.func.isRequired,
  handleNavigation: PropTypes.func.isRequired,
  isCollapsed: PropTypes.bool,
  isPinned: PropTypes.bool,
  onPinToggle: PropTypes.func.isRequired,
};

export default React.memo(SidebarContent);
